import React, { useState, useMemo } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { calculateWeight } from '@techchange/dnd';
import { messages } from '../../../i18n';

import TemplateSignature from './TemplateSignature';
import TemplateSignatureForm from './TemplateSignatureForm';

import styles from './TemplateSignatures.module.scss';

const SIGNATURE_LIMIT = 2;

const axiosClient = axios.create({
	withCredentials: true,
});

const propTypes = {

	/**
	 * The list of people associated with the certificate template.
	 */
	signatures: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The certificate person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The certificate person's weight.
		 */
		weight: PropTypes.number.isRequired,

		/**
		 * The ID of the associated certificate template.
		 */
		template_id: PropTypes.string,

		/**
		 * The ID of the associated person.
		 */
		person_id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,


	})).isRequired,

	/**
	 * The list of all available people for this organization.
	 */
	people: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	})).isRequired,

	/**
	 * The certificate template ID (only if editing an existing template).
	 */
	templateID: PropTypes.string,

	/**
	 * The filestack credentials object.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Callback function to handle changes to list of
	 * available organization people.
	 */
	onPersonChange: PropTypes.func.isRequired,

	/**
	 * Callback function to update the template signature list.
	 */
	onChange: PropTypes.func.isRequired,

};

const TemplateSignatures = (props) => {

	const {
		signatures,
		people,
		templateID,
		filestack,
		onPersonChange,
		onChange
	} = props;

	const [newSignature, setNewSignature] = useState(false);

	// Only allow at most two signatures
	const belowLimit = signatures ? signatures.length < SIGNATURE_LIMIT : true;

	const onAdd = (person) => {

		const after = signatures.length > 0
			? signatures[signatures.length - 1].weight
			: null;

		const certificatePerson = {
			weight: calculateWeight(after, null),
			person_id: person.id,
			name: person.name,
			affiliation: person.affiliation,
			signature: person.signature,
		};

		if (templateID) {
			certificatePerson.template_id = templateID;
		}

		const updatedSignatures = [...signatures, certificatePerson];

		setNewSignature(false);

		onChange('certificate_people', updatedSignatures);

	};

	const onCreate = (person) => {

		// Create the new organization person
		axiosClient.post('/api/v2/people', person).then(({ data }) => {

			const certificatePerson = {
				person_id: data.id,
				name: data.name,
				affiliation: data.affiliation,
				signature: data.signature,
			};

			if (templateID) {
				certificatePerson.template_id = templateID;
			}

			// Update list of organization people
			onPersonChange(data);

			// Update the template signature list
			onAdd(data);

		});

	};

	const onEdit = (person) => {

		// Update the organization person
		axiosClient.patch(`/api/v2/people/${person.id}`, person).then(({ data }) => {

			const updatedSignatures = [...signatures];

			const currentSignatureIdx =
				signatures.findIndex((sig) => sig.person_id === data.id);

			if (currentSignatureIdx >= 0) {

				const currentSignature = signatures[currentSignatureIdx];

				const certificatePerson = {
					person_id: data.id,
					weight: currentSignature.weight,
					name: data.name,
					affiliation: data.affiliation,
					signature: data.signature,
				};

				if (templateID) {
					certificatePerson.template_id = templateID;
				}

				updatedSignatures[currentSignatureIdx] = certificatePerson;

			}

			// Update list of organization people
			onPersonChange(data);

			// Update the template signature list
			onChange('certificate_people', updatedSignatures);

		});

	};

	const onDelete = (certificatePersonID) => {

		// Remove the signature from the list
		const index = signatures.findIndex(
			(signature) => signature.id === certificatePersonID);

		if (index >= 0) {

			const updatedSignatures = [...signatures];

			updatedSignatures.splice(index, 1);

			onChange('certificate_people', updatedSignatures);

		}

	};

	// Filter out people that already have a signature
	const filteredPeople = useMemo(() => {

		const signaturePeopleIds = signatures.map((sig) => sig.person_id);

		return people.filter((person) => !signaturePeopleIds.includes(person.id));

	}, [signatures, people]);

	return (
		<div className={styles['template-signatures-list']}>
			{signatures.map((data) => (
				<TemplateSignature
					key={data.id}
					templateSignature={data}
					people={filteredPeople}
					filestack={filestack}
					onEdit={onEdit}
					onDelete={onDelete} />
			))}
			{!newSignature && belowLimit &&
				<div
					className="icon-btn"
					onClick={() => setNewSignature(true)}>
					<span className="pr-2">
						<i className="fal fa-circle-plus" />
					</span>
					<FormattedMessage
						{...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_BUTTON_TEXT_ADD_NEW_SIGNATURE} />
				</div>
			}
			{newSignature &&
				<div className="template-signature">
					<TemplateSignatureForm
						people={filteredPeople}
						filestack={filestack}
						onAdd={onAdd}
						onCreate={onCreate}
						onCancel={() => setNewSignature(false)} />
				</div>
			}
		</div>
	);

};

TemplateSignatures.propTypes = propTypes;

export default TemplateSignatures;
